import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyTodayAttendance } from '@/services/attendance.service';
import { useAuthStore } from '@/stores/auth-store';
import { resolvePunchFlags } from '@/lib/punch-events';
import type { AttendanceRecordApiRow } from '@/types/attendance';

export const PUNCH_STATUS_KEY = ['my-today-attendance'] as const;

export type PunchClockState = 'ready' | 'in';

/** Multi-punch: alternate ready (can punch in) ↔ in (can punch out). */
export function resolvePunchState(
  today: AttendanceRecordApiRow | null | undefined,
): PunchClockState {
  const flags = resolvePunchFlags(today);
  return flags.canPunchOut ? 'in' : 'ready';
}

export function usePunchStatus() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const query = useQuery({
    queryKey: PUNCH_STATUS_KEY,
    queryFn: getMyTodayAttendance,
    staleTime: 30_000,
    enabled: isAuthenticated,
  });

  const today = query.data ?? null;
  const flags = resolvePunchFlags(today);
  const state = resolvePunchState(today);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: PUNCH_STATUS_KEY });

  return {
    today,
    state,
    punchEvents: today?.punchEvents ?? [],
    shiftStart: flags.shiftStart,
    shiftEnd: flags.shiftEnd,
    openPunchInAt: flags.openPunchInAt,
    isPunchedIn: flags.isPunchedIn,
    isDoneForDay: flags.dayClosed,
    dayClosed: flags.dayClosed,
    canPunchIn: flags.canPunchIn,
    canPunchOut: flags.canPunchOut,
    ...query,
    invalidate,
  };
}
